import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import ConstructionIcon from '@mui/icons-material/Construction'
import PersonIcon from '@mui/icons-material/Person'
import SchoolIcon from '@mui/icons-material/School'
import { Alert, Box, Card, CardContent, CircularProgress, Container, FormControlLabel, Grid2 as Grid, Switch, Tab, Tabs, Typography } from '@mui/material'
import { useAtomValue } from 'jotai'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import useBar from '../components/common/Bar'
import GroupSessionTable from '../components/tables/GroupSessionTable'
import PrivateSessionTable from '../components/tables/PrivateSessionTable'
import TripTable from '../components/tables/TripTable'
import UserTable from '../components/tables/UserTable'
import { useUsers } from '../contexts/UserContext'
import { isAdminAtom } from '../domains/auth/AuthSelectors'
import { usersAtom } from '../domains/user/AtomUsers'
import MaintenanceService from '../services/MaintenanceService'
import CountUp from '../utils/CountUp'

// TabPanel 组件
function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ mt: 2 }}>
      {value === index && children}
    </Box>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const isAdmin = useAtomValue(isAdminAtom)
  const bar = useBar()

  const { initUsers, fetchUsersLoading, fetchUsersError } = useUsers()
  const users = useAtomValue(usersAtom)
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalInstructors: 0,
    totalAdmins: 0,
  })
  const [tabValue, setTabValue] = useState(0)
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [maintenanceLoading, setMaintenanceLoading] = useState(false)
  const [maintenanceError, setMaintenanceError] = useState(null)

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  // Check if user is admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/')
    }
  }, [isAdmin, navigate])

  // Initialize users data only once when component mounts
  useEffect(() => {
    initUsers()
  }, [initUsers])

  // Fetch maintenance mode status on component mount
  useEffect(() => {
    const fetchMaintenanceMode = async () => {
      try {
        setMaintenanceLoading(true)
        setMaintenanceError(null)
        const response = await MaintenanceService.getMaintenanceMode()
        setMaintenanceMode(response.enabled)
      } catch (error) {
        console.error('Failed to fetch maintenance mode:', error)
        setMaintenanceError(error.message || 'Failed to load maintenance mode status')
      } finally {
        setMaintenanceLoading(false)
      }
    }

    fetchMaintenanceMode()
  }, [])

  // Handle maintenance mode toggle
  const handleMaintenanceModeToggle = async (event) => {
    const newValue = event.target.checked
    try {
      setMaintenanceLoading(true)
      setMaintenanceError(null)
      const response = await MaintenanceService.setMaintenanceMode(newValue)
      setMaintenanceMode(response.enabled)
      bar.success(`Maintenance mode ${response.enabled ? 'enabled' : 'disabled'}`)
    } catch (error) {
      console.error('Failed to set maintenance mode:', error)
      const errorMessage = error.message || 'Failed to update maintenance mode'
      setMaintenanceError(errorMessage)
      bar.error(errorMessage)
      // Revert the switch state on error
      setMaintenanceMode(!newValue)
    } finally {
      setMaintenanceLoading(false)
    }
  }

  // Calculate statistics when usersList changes
  useEffect(() => {
    const userList = Object.values(users)

    if (userList && userList.length > 0) {
      const totalInstructors = userList.filter((user) => user.roles.includes('INSTRUCTOR')).length
      const totalAdmins = userList.filter((user) => user.roles.includes('ADMIN')).length
      setStats({
        totalUsers: userList.length,
        totalInstructors,
        totalAdmins,
      })
    }
  }, [users])

  if (fetchUsersLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    )
  }

  if (fetchUsersError) {
    return (
      <Box p={3}>
        <Alert severity="error">{fetchUsersError}</Alert>
      </Box>
    )
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>

        {/* Statistics Cards */}
        <Grid container alignContent={'center'} justifyContent={'center'} spacing={3} sx={{ mb: 4 }}>
          {/* Total Users Card */}
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <Card
              onClick={() => navigate('/users')}
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <PersonIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" component="div">
                      <CountUp from={0} to={stats.totalUsers} separator="," direction="up" duration={1} className="count-up-text" />
                    </Typography>
                    <Typography color="text.secondary">Total Users</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Instructors Card */}
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <Card
              onClick={() => navigate('/instructors')}
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <SchoolIcon color="secondary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" component="div">
                      <CountUp from={0} to={stats.totalInstructors} separator="," direction="up" duration={1} className="count-up-text" />
                    </Typography>
                    <Typography color="text.secondary">Total Instructors</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Admins Card */}
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <AdminPanelSettingsIcon color="error" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" component="div">
                      <CountUp from={0} to={stats.totalAdmins} separator="," direction="up" duration={1} className="count-up-text" />
                    </Typography>
                    <Typography color="text.secondary">Total Admins</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Maintenance Mode Card */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                  <Box display="flex" alignItems="center" gap={2} flex={1}>
                    <ConstructionIcon color={maintenanceMode ? 'warning' : 'disabled'} sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h6" component="div">
                        Maintenance Mode
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {maintenanceMode ? 'Enabled' : 'Disabled'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    {maintenanceLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <FormControlLabel control={<Switch checked={maintenanceMode} onChange={handleMaintenanceModeToggle} color="warning" />} label="" sx={{ m: 0 }} />
                    )}
                  </Box>
                </Box>
                {maintenanceError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {maintenanceError}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card>
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="dashboard tabs">
                <Tab label="User Management" />
                <Tab label="Private Sessions" />
                <Tab label="Group Sessions" />
                <Tab label="Trips" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <UserTable />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <PrivateSessionTable />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <GroupSessionTable />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <TripTable />
            </TabPanel>
          </CardContent>
        </Card>
      </Box>
    </Container>
  )
}
